import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

// ─────────────────── UI Components ───────────────────────────
import Modal from "../../common/Modal";
import PrimaryButton, { PrimaryButtonType } from "../../common/PrimaryButton";
import OutlinedButton from "../../common/OutlinedButton";
import Spinner from "../../common/Spinner";
import TiersInformation from "../../../pages/organizations/settings/components/TiersInformation";
import ClientCredentialsModal from "../ClientCredentialsModal/ClientCredentialsModal";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";

// ─────────────────── Styles ──────────────────────────────────
import styles from "./OrganizationRequestDetailsModal.module.scss";
import checkboxStyles from "../../common/hookForm/Checkbox/Checkbox.module.scss";

// ─────────────────── Store ───────────────────────────────────
import { useOrganisationStore } from "../../../providers/organisation/organisation.provider";

// ─────────────────── API ─────────────────────────────────────
import {
  createOrganizationClient,
  getOrganizationDocumentations,
} from "../../../setup/api/services/organization";

// ─────────────────── Types ───────────────────────────────────
import { IOrganizations } from "../../../setup/types/Organisation";
import {
  IOrgClientCreated,
  IOrganizationDocumentation,
} from "../../../setup/api/services/organization/models";

// ─────────────────── Utils ───────────────────────────────────
import {
  ORGANIZATION_REQUEST_STATUS_LABELS,
  canReviewOrganizationRequest,
  getOrganizationRequestStatus,
} from "../../../setup/util/organizationRequest";

interface Props {
  showModal: boolean;
  closeModal: () => void;
  organization?: IOrganizations;
  /** Opens straight into the reject-reason step (row-level "رفض") instead of
   * the accept/reject buttons (row click / org name — "view details"). */
  initialShowRejectInput?: boolean;
  /** Immediately runs the accept flow once opened (row-level "قبول"), so the
   * fee-setup success step still appears instead of only updating the row. */
  autoAccept?: boolean;
}

const formatRequestDate = (date?: string) =>
  date
    ? new Date(date).toLocaleDateString("ar-EG", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

/** "SA4480000000012345678912" → "SA44 8000 0000 0123 4567 8912". */
const formatIban = (iban?: string) =>
  iban
    ? iban
        .replace(/\s/g, "")
        .replace(/(.{4})/g, "$1 ")
        .trim()
    : "-";

const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <path d="M14 2.5H7A2 2 0 0 0 5 4.5v15a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7.5l-5-5Z" />
    <path d="M14 2.5v5h5" />
    <path d="M9 12h6M9 16h4" />
  </svg>
);

const OrganizationRequestDetailsModal = ({
  showModal,
  closeModal,
  organization,
  initialShowRejectInput = false,
  autoAccept = false,
}: Props) => {
  const { t } = useTranslation();
  const [showRejectInput, setShowRejectInput] = useState(
    initialShowRejectInput
  );
  const [reason, setReason] = useState("");
  // After a successful accept, the modal switches to letting the admin configure the
  // Waffy Agreement fee template/tiers right away instead of just closing — see
  // TiersInformation below. Configuring it is optional; the admin can always finish it
  // later from the organization's own Settings → "اتفاقية وفّي" tab.
  const [showFeeSetup, setShowFeeSetup] = useState(false);

  // Optional, unchecked by default — not every org needs a partner API client (design doc
  // §9 Phase 2). When checked, a client is created right after acceptance so staff can hand
  // over ready-to-use credentials in the same interaction, instead of telling the org to go
  // create one themselves in the portal. Same endpoint as the portal's self-service creation
  // and the org settings page's own "create client" button — always auto-accept-consent=false.
  const [createClientOnAccept, setCreateClientOnAccept] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [createdCredentials, setCreatedCredentials] =
    useState<IOrgClientCreated | null>(null);

  // Supporting documents the organization uploaded with its request (IBAN letter,
  // national address, commercial register, …). Same source as the organization's
  // Settings → Documentation tab, so whatever the admin sees there shows up here.
  const [documents, setDocuments] = useState<IOrganizationDocumentation[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  const buttonLoading = useOrganisationStore((state) => state.buttonLoading);
  const verifyOrganisation = useOrganisationStore(
    (state) => state.verifyOrganisation
  );
  const rejectOrganisation = useOrganisationStore(
    (state) => state.rejectOrganisation
  );

  // Reset the reject-reason step whenever a different org is opened / the modal closes,
  // respecting the caller's requested initial step (row-level Reject vs. view details).
  useEffect(() => {
    setShowRejectInput(initialShowRejectInput);
    setReason("");
    setShowFeeSetup(false);
    setCreateClientOnAccept(false);
    setCreatingClient(false);
    setCreatedCredentials(null);
  }, [organization?.code, showModal, initialShowRejectInput]);

  useEffect(() => {
    const code = organization?.code;
    if (!showModal || !code) {
      setDocuments([]);
      return;
    }

    let cancelled = false;
    setDocumentsLoading(true);
    getOrganizationDocumentations(code)
      .then((response) => {
        if (!cancelled) setDocuments(response.data || []);
      })
      .catch((error) => {
        // A failed fetch is treated as "no documents", which also keeps the
        // accept/reject actions disabled — better than approving blind.
        console.error("Error fetching organization request documents:", error);
        if (!cancelled) setDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setDocumentsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showModal, organization?.code]);

  const handleClose = () => {
    closeModal();
  };

  const handleAccept = useCallback(async () => {
    if (!organization?.code) return;
    const accepted = await verifyOrganisation(organization.code);
    if (!accepted) {
      // verifyOrganisation already surfaced a toast — keep the modal open on the
      // accept/reject step so the admin can retry instead of silently closing it.
      return;
    }
    if (!createClientOnAccept) {
      setShowFeeSetup(true);
      return;
    }
    setCreatingClient(true);
    try {
      const response = await createOrganizationClient(organization.code);
      setCreatedCredentials(response?.data ?? null);
      // The fee-setup step still follows once the admin closes the credentials modal —
      // see the ClientCredentialsModal handleClose below.
    } catch (error) {
      console.error(error);
      toast.error(t("lbl_create_client_failed"));
      setShowFeeSetup(true);
    } finally {
      setCreatingClient(false);
    }
  }, [organization?.code, verifyOrganisation, createClientOnAccept, t]);

  // Row-level "قبول" opens the modal with autoAccept instead of calling verifyOrganisation
  // directly, so this runs the exact same accept flow (and fee-setup success step) as
  // clicking "قبول وتفعيل المؤسسة" from inside the modal.
  useEffect(() => {
    if (!showModal || !autoAccept || !organization?.code) return;
    handleAccept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, autoAccept, organization?.code]);

  const handleConfirmReject = async () => {
    if (!organization?.code || !reason.trim()) return;
    await rejectOrganisation(organization.code, reason.trim());
    handleClose();
  };

  const openDocument = (url?: string) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const isPending = organization?.verificationStatus === "PENDING";
  const isRejected = organization?.verificationStatus === "REJECTED";
  const admin = organization?.organizationAdmin;
  const bankAccount = organization?.bankAccount;
  const address = organization?.address;

  // A request can't be judged before its documents, bank account and national address
  // are in. While documents are still loading we also hold the actions, so the admin
  // never acts on an unknown state.
  const hasDocuments = documents.length > 0;
  const canReview =
    canReviewOrganizationRequest(organization, hasDocuments) &&
    !documentsLoading;
  const status = getOrganizationRequestStatus(organization, hasDocuments);

  return (
    <>
      <Modal
        size={showFeeSetup ? "large" : undefined}
        customModalClassName={
          showFeeSetup ? styles.feeSetupModal : styles.requestModal
        }
        visible={showModal}
        onCancel={handleClose}
      >
        {showFeeSetup ? (
          <div className={`${styles.container} ${styles.feeSetupContainer}`}>
            <div className={styles.feeSetupHeader}>
              <div className={styles.successBanner}>
                <CheckCircleOutlinedIcon />
                <span>
                  {t("lbl_org_request_accept_success", {
                    val: organization?.name,
                  })}
                </span>
              </div>
              <p>{t("lbl_org_request_fee_setup_hint")}</p>
            </div>
            <TiersInformation
              organization={organization}
              isEditable
              secondaryAction={
                <OutlinedButton onClick={handleClose}>
                  {t("lbl_close")}
                </OutlinedButton>
              }
            />
          </div>
        ) : (
          <div className={styles.container}>
            <div className={styles.head}>
              <h3>{t("lbl_org_request_title")}</h3>
              <span>
                {t("lbl_org_request_number_prefix")}{" "}
                <span className={styles.ltr}>#{organization?.code}</span> ·{" "}
                {formatRequestDate(organization?.createdAt)}
              </span>
              {/* Held back until the documents resolve — the label depends on them,
                and flashing "قيد المراجعة" first would be misleading. */}
              {!documentsLoading && (
                <div>
                  <span
                    className={`${styles.chip} ${
                      styles[`chip_${status.toLowerCase()}`]
                    }`}
                  >
                    {ORGANIZATION_REQUEST_STATUS_LABELS[status]}
                  </span>
                </div>
              )}
            </div>

            <div className={styles.section}>
              <h4>{t("lbl_org_request_applicant_info")}</h4>
              <div className={styles.grid}>
                <div className={styles.item}>
                  <b>{t("lbl_name")}</b>
                  <span>{admin?.profile?.fullName || "-"}</span>
                </div>
                <div className={styles.item}>
                  <b>{t("lbl_mobile_number")}</b>
                  <span className={styles.ltr}>
                    {admin?.phoneNumber || "-"}
                  </span>
                </div>
                <div className={styles.item}>
                  <b>{t("email")}</b>
                  <span className={styles.ltr}>{admin?.email || "-"}</span>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h4>{t("lbl_org_request_organization_info")}</h4>
              <div className={styles.grid}>
                <div className={styles.item}>
                  <b>{t("lbl_org_request_organization_name")}</b>
                  <span className={styles.orgName}>
                    {organization?.logoUrl && (
                      <img
                        className={styles.logo}
                        src={organization.logoUrl}
                        alt={organization.name}
                      />
                    )}
                    {organization?.name || "-"}
                  </span>
                </div>
                <div className={styles.item}>
                  <b>{t("lbl_org_request_website_url")}</b>
                  {organization?.websiteUrl ? (
                    <a
                      className={`${styles.link} ${styles.ltr}`}
                      href={organization.websiteUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {organization.websiteUrl}
                    </a>
                  ) : (
                    <span>-</span>
                  )}
                </div>
              </div>
              <div className={`${styles.item} ${styles.descriptionItem}`}>
                <b>{t("lbl_org_request_description")}</b>
                <span>{organization?.description || "-"}</span>
              </div>
              <div className={`${styles.grid} ${styles.descriptionItem}`}>
                <div className={styles.item}>
                  <b>{t("lbl_org_request_cr_number")}</b>
                  <span className={styles.ltr}>
                    {organization?.commercialRegisterNumber || "-"}
                  </span>
                </div>
                <div className={styles.item}>
                  <b>{t("lbl_org_request_unified_number")}</b>
                  <span className={styles.ltr}>
                    {organization?.unifiedNumber || "-"}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h4>{t("lbl_org_request_bank_account")}</h4>
              {bankAccount?.iban ? (
                <div className={styles.grid}>
                  <div className={styles.item}>
                    <b>{t("lbl_org_request_iban")}</b>
                    <span className={styles.ltr}>
                      {formatIban(bankAccount.iban)}
                    </span>
                  </div>
                  <div className={styles.item}>
                    <b>{t("lbl_org_request_beneficiary_name")}</b>
                    <span>{bankAccount.beneficiaryName || "-"}</span>
                  </div>
                  <div className={styles.item}>
                    <b>{t("lbl_org_request_bank")}</b>
                    {/* An unvalidated IBAN is saved against the backend's placeholder "NAN"
                        bank, whose name means nothing — show "-" instead. */}
                    <span>
                      {bankAccount.validated
                        ? bankAccount.bank?.arabicName ||
                          bankAccount.bank?.englishName ||
                          "-"
                        : "-"}
                    </span>
                  </div>
                  <div className={styles.item}>
                    <b>{t("lbl_org_request_iban_validation")}</b>
                    {/* Unvalidated = the IBAN didn't match the CR / unified number, or the
                        check was unavailable. It's saved anyway for the admin to verify here,
                        against the uploaded IBAN certificate. */}
                    <span
                      className={`${styles.chip} ${styles.inlineChip} ${
                        bankAccount.validated
                          ? styles.chip_verified
                          : styles.chip_pending
                      }`}>
                      {bankAccount.validated
                        ? t("lbl_org_request_iban_validated")
                        : t("lbl_org_request_iban_not_validated")}
                    </span>
                  </div>
                </div>
              ) : (
                <p className={styles.noDocuments}>
                  {t("lbl_org_request_no_bank_account")}
                </p>
              )}
            </div>

            <div className={styles.section}>
              <h4>{t("lbl_org_request_national_address")}</h4>
              {address?.nationalAddress ? (
                <div className={styles.grid}>
                  <div className={styles.item}>
                    <b>{t("lbl_org_request_address_code")}</b>
                    <span className={styles.ltr}>
                      {address.nationalAddress}
                    </span>
                  </div>
                  <div className={styles.item}>
                    <b>{t("lbl_org_request_address_region")}</b>
                    <span>{address.state || address.province || "-"}</span>
                  </div>
                  <div className={styles.item}>
                    <b>{t("lbl_org_request_address_city")}</b>
                    <span>{address.city || "-"}</span>
                  </div>
                  <div className={styles.item}>
                    <b>{t("lbl_org_request_address_district")}</b>
                    <span>{address.district || "-"}</span>
                  </div>
                  <div className={styles.item}>
                    <b>{t("lbl_org_request_address_street")}</b>
                    <span>{address.street || "-"}</span>
                  </div>
                  <div className={styles.item}>
                    <b>{t("lbl_org_request_address_postal_code")}</b>
                    <span className={styles.ltr}>
                      {address.postalCode || "-"}
                    </span>
                  </div>
                  {address.buildingNo && (
                    <div className={styles.item}>
                      <b>{t("lbl_org_request_address_building_no")}</b>
                      <span className={styles.ltr}>{address.buildingNo}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className={styles.noDocuments}>
                  {t("lbl_org_request_no_address")}
                </p>
              )}
            </div>

            <div className={styles.section}>
              <h4>{t("lbl_org_request_documents")}</h4>
              {documentsLoading ? (
                <div className={styles.documentsLoading}>
                  <Spinner />
                </div>
              ) : documents.length === 0 ? (
                <p className={styles.noDocuments}>
                  {t("lbl_org_request_no_documents")}
                </p>
              ) : (
                documents.map((doc) => (
                  <div key={doc.id} className={styles.doc}>
                    <span className={styles.docIcon}>
                      <DocumentIcon />
                    </span>
                    <span className={styles.docText}>
                      <b>{doc.title}</b>
                      <span>{doc.description || doc.attachmentUrl}</span>
                    </span>
                    <button
                      className={styles.docView}
                      type="button"
                      disabled={!doc.attachmentUrl}
                      onClick={() => openDocument(doc.attachmentUrl)}
                    >
                      {t("lbl_org_request_view_document")}
                    </button>
                  </div>
                ))
              )}
            </div>

            {isRejected && (
              <div className={styles.rejectedBox}>
                <strong>{t("lbl_org_request_rejection_reason")}</strong>
                <p>{organization?.rejectionReason || "-"}</p>
              </div>
            )}

            {isPending && showRejectInput && (
              <div className={styles.rejectPanel}>
                <label htmlFor="organization-reject-reason">
                  {t("lbl_org_request_rejection_reason")}
                </label>
                <textarea
                  id="organization-reject-reason"
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t(
                    "lbl_org_request_rejection_reason_placeholder"
                  )}
                  autoFocus
                />
                <div className={styles.actions}>
                  <PrimaryButton
                    buttonType={PrimaryButtonType.DANGER}
                    disabled={buttonLoading || !reason.trim() || !canReview}
                    onClick={handleConfirmReject}
                  >
                    {buttonLoading ? (
                      <Spinner />
                    ) : (
                      t("lbl_org_request_confirm_reject")
                    )}
                  </PrimaryButton>
                  <OutlinedButton
                    disabled={buttonLoading}
                    onClick={() => setShowRejectInput(false)}
                  >
                    {t("lbl_cancel")}
                  </OutlinedButton>
                </div>
              </div>
            )}

            {isPending && !showRejectInput && (
              <>
                {!documentsLoading && !canReview && (
                  <p className={styles.noDocumentsHint}>
                    {t("lbl_org_request_no_documents_hint")}
                  </p>
                )}
                <div
                  className={checkboxStyles.checkboxContainer}
                  style={{ marginBottom: 12 }}
                >
                  <div
                    className={`${checkboxStyles.checkboxBox} ${
                      createClientOnAccept ? checkboxStyles.checked : ""
                    } ${
                      buttonLoading || creatingClient
                        ? checkboxStyles.disabled
                        : ""
                    }`}
                    onClick={() =>
                      !(buttonLoading || creatingClient) &&
                      setCreateClientOnAccept((prev) => !prev)
                    }
                    role="checkbox"
                    aria-checked={createClientOnAccept}
                    tabIndex={buttonLoading || creatingClient ? -1 : 0}
                    onKeyDown={(e) => {
                      if (
                        (e.key === "Enter" || e.key === " ") &&
                        !(buttonLoading || creatingClient)
                      ) {
                        e.preventDefault();
                        setCreateClientOnAccept((prev) => !prev);
                      }
                    }}
                  >
                    {createClientOnAccept && (
                      <img
                        src="/assets/images/svg/tick_white.svg"
                        alt="checked"
                        className={checkboxStyles.tickIcon}
                      />
                    )}
                  </div>
                  <label
                    className={checkboxStyles.label}
                    onClick={() =>
                      !(buttonLoading || creatingClient) &&
                      setCreateClientOnAccept((prev) => !prev)
                    }
                  >
                    {t("lbl_create_client_for_org_now")}
                  </label>
                </div>
                <div className={styles.actions}>
                  <PrimaryButton
                    style={{ flex: 1.4, width: "100%" }}
                    onClick={handleAccept}
                    disabled={buttonLoading || creatingClient || !canReview}
                  >
                    {buttonLoading || creatingClient ? (
                      <Spinner />
                    ) : (
                      t("lbl_org_request_accept_and_activate")
                    )}
                  </PrimaryButton>
                  <button
                    className={styles.dangerOutlineBtn}
                    type="button"
                    disabled={buttonLoading || creatingClient || !canReview}
                    onClick={() => setShowRejectInput(true)}
                  >
                    {t("lbl_org_request_reject")}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
      <ClientCredentialsModal
        open={!!createdCredentials}
        credentials={createdCredentials}
        handleClose={() => {
          setCreatedCredentials(null);
          setShowFeeSetup(true);
        }}
      />
    </>
  );
};

export default OrganizationRequestDetailsModal;
